import { LARGE_PAGE_REF_THRESHOLD } from '../constants.js';
import type { SnapshotElement } from '../snapshot-analyzer.js';

export type CheckId =
  | 'editability'
  | 'gridCellValue'
  | 'formFieldLabel'
  | 'filterState'
  | 'modalV2'
  | 'iconButton'
  | 'genericClickable'
  | 'clickableImg'
  | 'toggle'
  | 'testId'
  | 'dataId'
  | 'dataField'
  | 'svgPromote'
  | 'dialogModality'
  | 'hclip';

export interface PerRefAugmentationOpts {
  /** When true, also run v2-only checks (modalV2) and return overrides. */
  v2?: boolean;
  /**
   * Refs of large grids the section formatter will collapse. Cells
   * inside these grids are skipped — no point augmenting refs the
   * agent will never see.
   */
  skipGridRefs?: Set<string>;
  /** Avoid re-parsing if the caller already did. */
  preParsedElements?: SnapshotElement[];
  /** Stops scheduling additional Playwright work after snapshot cancellation. */
  signal?: AbortSignal;
}

export interface PerRefArgs {
  checks: CheckId[];
  /** Search-textbox value for filterState; only set for that check. */
  searchValue?: string;
}

export const FORM_FIELD_ROLES = new Set(['textbox', 'combobox', 'searchbox']);

const SEARCH_TEXTBOX_NAME_RE = /search|filter|find|lookup/i;
const INTERACTIVE_ROLES = new Set([
  'button',
  'link',
  'textbox',
  'combobox',
  'searchbox',
  'checkbox',
  'switch',
  'radio',
  'menuitem',
  'tab',
]);

const CONTAINER_ROLES_FOR_ID = new Set([
  'generic',
  'group',
  'region',
  'article',
  'section',
  'listitem',
  'rowgroup',
  'list',
  'navigation',
  'complementary',
  'form',
]);

const PRIVATE_USE_GLYPH_RE = /[\uE000-\uF8FF]/g;
const HAS_PRIVATE_USE_GLYPH_RE = /[\uE000-\uF8FF]/;

// Sub-frame ref shape (`f<frameIdx>e<elemIdx>`). Refs of this shape
// don't resolve via `aria-ref=` against the page scope — they live
// inside iframes. Filtering them at planning time avoids polluting
// the null-rate diagnostic and saves CDP queries.
const SUB_FRAME_REF_RE = /^f\d+e\d+$/;

function hasOnlyPrivateUseGlyphs(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed || !HAS_PRIVATE_USE_GLYPH_RE.test(trimmed)) return false;
  return trimmed.replace(PRIVATE_USE_GLYPH_RE, '').trim() === '';
}

/**
 * Build the subtree-interactivity index in one iterative post-order pass.
 * `planPerRefChecks` consults this set for both modal and data-id candidates
 * instead of recursively re-walking every generic/container subtree.
 */
function indexInteractiveSubtrees(elements: SnapshotElement[]): WeakSet<SnapshotElement> {
  const interactiveSubtrees = new WeakSet<SnapshotElement>();
  const stack: Array<{ element: SnapshotElement; visited: boolean }> = [];
  for (let index = elements.length - 1; index >= 0; index--) {
    stack.push({ element: elements[index], visited: false });
  }

  while (stack.length > 0) {
    const entry = stack.pop();
    if (!entry) break;
    const { element, visited } = entry;
    if (!visited) {
      stack.push({ element, visited: true });
      for (let index = element.children.length - 1; index >= 0; index--) {
        stack.push({ element: element.children[index], visited: false });
      }
      continue;
    }

    if (
      INTERACTIVE_ROLES.has(element.role) ||
      element.children.some((child) => interactiveSubtrees.has(child))
    ) {
      interactiveSubtrees.add(element);
    }
  }

  return interactiveSubtrees;
}

/**
 * Walk the parsed tree, deciding which CheckIds to run for each ref.
 * For modalV2, candidates are pre-filtered using parsed-tree signals
 * (must contain an interactive descendant) so a Tailwind/shadcn page
 * with hundreds of role-less DIVs doesn't ship hundreds of CDP
 * queries. Heading is treated as a name-source signal, not a gate —
 * alert dialogs and confirms with no `<h*>` should still be promoted.
 */
export function planPerRefChecks(
  elements: SnapshotElement[],
  opts: PerRefAugmentationOpts
): Array<{ ref: string; args: PerRefArgs }> {
  const planByRef = new Map<string, PerRefArgs>();
  const skipGridRefs = opts.skipGridRefs;
  const interactiveSubtrees = indexInteractiveSubtrees(elements);
  // Targetable refs eligible for the horizontal-clip check. Collected during the
  // walk and only scheduled if the page is small enough — on a huge page the
  // ancestor-walk-per-ref would add to augmentation cost (the executor emits a
  // [Large page] marker there instead).
  const hclipCandidates: string[] = [];

  const ensure = (ref: string): PerRefArgs => {
    let args = planByRef.get(ref);
    if (!args) {
      args = { checks: [] };
      planByRef.set(ref, args);
    }
    return args;
  };
  const addCheck = (ref: string, check: CheckId) => {
    if (SUB_FRAME_REF_RE.test(ref)) return;
    const args = ensure(ref);
    if (!args.checks.includes(check)) args.checks.push(check);
  };

  const workStack: Array<{ element: SnapshotElement; inGridCell: boolean }> = [];
  for (let index = elements.length - 1; index >= 0; index--) {
    workStack.push({ element: elements[index], inGridCell: false });
  }

  while (workStack.length > 0) {
    const entry = workStack.pop();
    if (!entry) break;
    const { element: el, inGridCell } = entry;
    if (el.role === 'grid' && el.ref && skipGridRefs?.has(el.ref)) continue;
    const isGridCell = el.role === 'gridcell';

    if (el.ref) {
      if (FORM_FIELD_ROLES.has(el.role)) {
        addCheck(el.ref, 'editability');
        addCheck(el.ref, 'formFieldLabel');

        const accName = el.text?.trim();
        const value = el.inputValue?.trim();
        if (value && accName && SEARCH_TEXTBOX_NAME_RE.test(accName)) {
          addCheck(el.ref, 'filterState');
          ensure(el.ref).searchValue = value;
        }

        // gridCellValue — textbox/combobox in a gridcell with no inputValue
        if (inGridCell && (el.role === 'textbox' || el.role === 'combobox') && !el.inputValue) {
          addCheck(el.ref, 'gridCellValue');
        }
      }

      // modalV2 candidate gate: role-less container with at least one
      // interactive descendant. Heading isn't required (alert dialogs
      // and confirms often have prose, not h*) but it'll be picked up
      // as augName by the page-side fn when present.
      if (opts.v2 && el.role === 'generic' && interactiveSubtrees.has(el)) {
        addCheck(el.ref, 'modalV2');
      }

      // dialogModality — verify whether a native `role=dialog`/`alertdialog`
      // is actually rendered/blocking. Apps leave `aria-modal=true` on
      // collapsed chat/announcer widgets, which otherwise demote the whole
      // page to background and crowd the dialog stack. Run for every dialog
      // (not just aria-modal ones) so the formatter can also drop inert
      // dialogs from the DIALOG STACK header.
      if (el.role === 'dialog' || el.role === 'alertdialog') {
        addCheck(el.ref, 'dialogModality');
      }

      // iconButton — empty or private-use-glyph-only text on a button/link.
      // Same predicate as legacy `shouldAugmentElementLabel`.
      if (
        (el.role === 'button' || el.role === 'link') &&
        (!el.text || hasOnlyPrivateUseGlyphs(el.text))
      ) {
        addCheck(el.ref, 'iconButton');
      }

      // genericClickable — `<div role="button">`-style affordances that
      // arrive as `generic [cursor=pointer]` with no text or text-bearing
      // descendants. Lucide icon-class enrichment for these was dead code
      // in production until this fold; the planner branch re-enables it.
      if (
        el.role === 'generic' &&
        !el.text &&
        el.attributes['cursor'] === 'pointer' &&
        el.children.every((c) => !c.text)
      ) {
        addCheck(el.ref, 'genericClickable');
      }

      // clickableImg — bare `<img cursor=pointer>` with no alt text on the
      // substrate line.
      if (el.role === 'img' && !el.text && el.attributes['cursor'] === 'pointer') {
        addCheck(el.ref, 'clickableImg');
      }

      // toggle — switch/checkbox; we always run, even when the substrate
      // line already carries a label, because the check also surfaces
      // [checked=…] / [disabled=…] state.
      if (el.role === 'switch' || el.role === 'checkbox') {
        addCheck(el.ref, 'toggle');
      }

      // testId — substrate-v2 only. Carry stable DOM test identifiers for
      // targetable controls whose accessibility labels are often absent or
      // framework-derived. The locator layer still filters volatile IDs.
      const isTargetableForId =
        INTERACTIVE_ROLES.has(el.role) ||
        el.role === 'option' ||
        el.role === 'row' ||
        el.role === 'gridcell' ||
        el.role === 'listitem' ||
        el.attributes['cursor'] === 'pointer';
      if (opts.v2 && isTargetableForId) {
        addCheck(el.ref, 'testId');
      }

      // hclip — clickable targets are where "scroll sideways to reach it"
      // matters (wide grids/carousels). Collected now, scheduled after the walk
      // iff the page is under the size gate.
      if (isTargetableForId) {
        hclipCandidates.push(el.ref);
      }

      // dataId — entity `data-id` on potential scope ancestors so the stable-id
      // ancestor path can run on real captures (not just interactive targets).
      // Independent of testId: schedule for targetable elements AND for
      // container roles that hold an interactive descendant (a panel/card/section
      // wrapping a button) — bounded so we don't probe every layout `<div>`.
      if (
        opts.v2 &&
        (isTargetableForId || (CONTAINER_ROLES_FOR_ID.has(el.role) && interactiveSubtrees.has(el)))
      ) {
        addCheck(el.ref, 'dataId');
      }

      // dataField — grid cells carry a stable `data-field` token (e.g. MUI
      // DataGrid `house_rate::GroupA`). Capture it as a row-scoped, semantic
      // locator anchor, and flag editability so the formatter can surface
      // empty editable cells the agent would otherwise only reach by
      // coordinate-clicking.
      if (opts.v2 && (el.role === 'gridcell' || el.role === 'cell')) {
        addCheck(el.ref, 'dataField');
      }

      // svgPromote — generic-role nodes that the pre-snapshot SVG
      // enricher tagged with `data-svg-enriched`. This is a second-pass
      // check (it depends on the post-augmentation tree's `text` field)
      // so it lands here only when text is already present *before* the
      // augmenter runs. The orchestrator runs a follow-up pass for
      // refs whose lines gained text during apply.
      if (el.role === 'generic' && el.ref && el.text) {
        addCheck(el.ref, 'svgPromote');
      }
    }

    const childInGridCell = inGridCell || isGridCell;
    for (let index = el.children.length - 1; index >= 0; index--) {
      workStack.push({ element: el.children[index], inGridCell: childInGridCell });
    }
  }

  // Schedule hclip only when the page is small enough that the extra
  // ancestor-walk-per-ref is cheap. On larger pages it's skipped (the executor
  // surfaces a [Large page] marker), keeping the wedge protection intact.
  if (hclipCandidates.length <= LARGE_PAGE_REF_THRESHOLD) {
    for (const ref of hclipCandidates) addCheck(ref, 'hclip');
  }

  return [...planByRef].map(([ref, args]) => ({ ref, args }));
}
